import type { i18n as I18nInstance } from 'i18next'

/**
 * Routes prerendered to static `{route}/index.html` at build time
 * (`scripts/prerender.mjs`) — see PR-109/AC-088. Content-stable public
 * pages only; competition/project detail pages are data-driven and are a
 * separate follow-up (Wave W Phase 2), not covered by this list.
 */
export const PRERENDER_ROUTES = [
  '/',
  '/about',
  '/howto',
  '/architecture',
  '/trust',
  '/privacy',
  '/terms',
  '/data-policy',
  '/guide/student',
  '/guide/judge',
  '/guide/organizer',
  '/guide/admin',
  '/help',
  '/help/student/final-submit',
  '/help/troubleshooting/submission-failed',
  '/help/organizer/invite-judge',
  '/help/organizer/reopen-score',
  '/help/reference/statuses',
] as const
export type PrerenderRoute = (typeof PRERENDER_ROUTES)[number]

const ROUTE_COPY: Record<PrerenderRoute, { ns: string; titleKey: string; descriptionKey: string }> =
  {
    '/': { ns: 'home', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/about': { ns: 'about', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/howto': { ns: 'howto', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/architecture': {
      ns: 'architecture',
      titleKey: 'hero.title',
      descriptionKey: 'hero.description',
    },
    '/trust': { ns: 'trust', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/privacy': { ns: 'privacy', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/terms': { ns: 'terms', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/data-policy': {
      ns: 'data-policy',
      titleKey: 'hero.title',
      descriptionKey: 'hero.description',
    },
    '/guide/student': {
      ns: 'guide-student',
      titleKey: 'hero.title',
      descriptionKey: 'hero.description',
    },
    '/guide/judge': {
      ns: 'guide-judge',
      titleKey: 'hero.title',
      descriptionKey: 'hero.description',
    },
    '/guide/organizer': {
      ns: 'guide-organizer',
      titleKey: 'hero.title',
      descriptionKey: 'hero.description',
    },
    '/guide/admin': {
      ns: 'guide-admin',
      titleKey: 'hero.title',
      descriptionKey: 'hero.description',
    },
    '/help': { ns: 'help', titleKey: 'hero.title', descriptionKey: 'hero.description' },
    '/help/student/final-submit': {
      ns: 'help-student-final-submit',
      titleKey: 'title',
      descriptionKey: 'description',
    },
    '/help/troubleshooting/submission-failed': {
      ns: 'help-student-submission-failed',
      titleKey: 'title',
      descriptionKey: 'description',
    },
    '/help/organizer/invite-judge': {
      ns: 'help-organizer-invite-judge',
      titleKey: 'title',
      descriptionKey: 'description',
    },
    '/help/organizer/reopen-score': {
      ns: 'help-organizer-reopen-score',
      titleKey: 'title',
      descriptionKey: 'description',
    },
    '/help/reference/statuses': {
      ns: 'help-reference-statuses',
      titleKey: 'title',
      descriptionKey: 'description',
    },
  }

export interface RouteMeta {
  title: string
  description: string
}

/**
 * Reuses the same locale copy the page itself renders (`hero.title`/
 * `hero.description`) rather than a hand-duplicated string, so the
 * server-rendered `<title>`/description can never drift from what a visitor
 * actually sees — the source SDD's anti-cloaking requirement (G-01/§3 in
 * the incoming spec) applies to metadata, not just body content.
 */
export function getRouteMeta(path: PrerenderRoute, instance: I18nInstance): RouteMeta {
  const copy = ROUTE_COPY[path]
  const t = instance.getFixedT(instance.language, copy.ns)
  const brand = instance.getFixedT(instance.language, 'common')('brand')
  const pageTitle = t(copy.titleKey)
  const description = t(copy.descriptionKey)
  return {
    title: path === '/' ? `${brand}｜${pageTitle}` : `${pageTitle}｜${brand}`,
    description,
  }
}
